import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AppParametricSelector, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {DraftMessageModel} from '../../../models/api/messageModel';
import {AppState} from '../../../store/appState';
import {fetchAccountsForCardsAsyncAction} from '../../../store/data/accounts/accountsActions';
import {getHasCompanyCardsEnabled} from '../../../store/data/cards/cardsSelectors';
import {getCompanyHasCsatSurveysFeature} from '../../../store/data/company/companySelectors';
import {
  makeGetRecipientCardForInterpolation,
  makeGetRecipientForInterpolation,
  makeGetVisualRecipientMap,
} from '../../../store/data/contacts/recipientsSelectors';
import {getConversations} from '../../../store/data/dataSelectors';
import {
  makeGetDraftMessageNamespace,
  makeGetVariableInterpolator,
} from '../../../store/data/messages/messagesSelectors';
import {
  toggleListAutoFormattingAction,
  updateDefaultFontStyleAction,
  updateLastEmailFormatAction,
} from '../../../store/data/preferences/preferencesActions';
import {
  getDefaultFontStyle,
  getListAutoFormatting,
} from '../../../store/data/preferences/preferencesSelectors';
import {makeGetSelectedSignature} from '../../../store/data/signatures/signaturesSelectors';
import {
  getComposerReplacePilledVariablesInMessageTemplates,
  getHasBulletedListsV2,
  getHasEmailGifs,
  getHasNativeDeleteInEmailComposer,
  getHasParagraphSpaceIndent,
  getHasRtlLanguageSupport,
  getHasTableEdition,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {getEmojiValidationMap} from '../../../store/stateless/emojiSelectors';
import {makeGetIsPersonalizedDemoInboxActive} from '../../../store/stateless/guidedDemoSelectors';
import {getI18n} from '../../../store/stateless/internationalizationSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {sanitizeDefaultFontStyle} from '../../common/editor/style/defaultFontStyle';
import {EmailComposer} from './emailComposer';

/*
 * Props.
 */

interface EmailComposerContainerProps {
  message: DraftMessageModel;
  channel: ChannelModel;
  conflictLink?: string;
}

/*
 * Selectors.
 */

function getMessage(_state: AppState, props: EmailComposerContainerProps) {
  return props.message;
}

function getChannel(_state: AppState, props: EmailComposerContainerProps) {
  return props.channel;
}

function getConversationId(_state: AppState, props: EmailComposerContainerProps) {
  return props.message.conversationId;
}

const getConversation = createSelector(
  [getConversationId, getConversations],
  (conversationId, conversations) => conversations[conversationId],
);

const getSanitizedDefaultFontStyle = createSelector(getDefaultFontStyle, sanitizeDefaultFontStyle);

function makeGetRecipientCardForInterpolationFromDraft<P>(
  getDraft: AppParametricSelector<P, DraftMessageModel>,
) {
  const getDraftNamespace = makeGetDraftMessageNamespace(getDraft);
  const getVisualRecipientMap = makeGetVisualRecipientMap(getDraftNamespace, getDraft);
  const getRecipientForInterpolation = makeGetRecipientForInterpolation(getVisualRecipientMap);
  return makeGetRecipientCardForInterpolation(
    getRecipientForInterpolation,
    getDraftNamespace,
    getHasCompanyCardsEnabled,
  );
}

/*
 * Component.
 */

const mapStateToProps = makeMapStateToProps<EmailComposerContainerProps>()(() => ({
  interpolator: makeGetVariableInterpolator(getMessage),
  selectedSignature: makeGetSelectedSignature(getChannel, getMessage),
  defaultFontStyle: getSanitizedDefaultFontStyle,
  hasCsatSurveysFeature: getCompanyHasCsatSurveysFeature,
  hasTableEdition: getHasTableEdition,
  hasParagraphSpaceIndent: getHasParagraphSpaceIndent,
  shouldShowDemoInboxTip: makeGetIsPersonalizedDemoInboxActive(getConversation),
  hasGifs: getHasEmailGifs,
  i18n: getI18n,
  emojiValidationMap: getEmojiValidationMap,
  hasListAutoFormatting: getListAutoFormatting,
  replacePilledVariablesInMessageTemplates: getComposerReplacePilledVariablesInMessageTemplates,
  cardForInterpolation: makeGetRecipientCardForInterpolationFromDraft(getMessage),
  hasRtlLanguageSupport: getHasRtlLanguageSupport,
  hasNativeDelete: getHasNativeDeleteInEmailComposer,
  hasListServiceV2: getHasBulletedListsV2,
}));

export const EmailComposerContainer = connect(mapStateToProps, {
  updateLastEmailFormat: updateLastEmailFormatAction,
  updateDefaultFontStyle: updateDefaultFontStyleAction,
  navigateToLinkAction,
  fetchAccountsForCardsAsyncAction,
  toggleListAutoFormatting: toggleListAutoFormattingAction,
})(EmailComposer);
